import { Column, Index, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import type { PdfTemplateDefinitionModel } from '../interfaces/pdf-template-definition';
import { BaseEntity } from './base.entity';
import { PdfTemplateCompanyEntity } from './pdf-template-company.entity';

/**
 * PD-14688 — which printed document a template lays out. Stored as a varchar
 * (not a DB enum) so adding document types never needs an ALTER. Invoices,
 * orders and quotes shipped first; statements followed (PD-16751).
 */
export enum PdfTemplateDocumentType {
	INVOICE = 'invoice',
	ORDER = 'order',
	QUOTE = 'quote',
	STATEMENT = 'statement',
}

/**
 * PD-14688 — a tenant-designed PDF layout for one document type, built in
 * the template builder and rendered by PdfService in place of the bundled
 * hardcoded template.
 *
 * Resolution order at render time: the template assigned to the document's
 * company (via pdf_template_company) → the tenant default for the document
 * type (isDefault) → the legacy bundled template. A tenant that never opens
 * the builder keeps exactly the documents it has today.
 */
@Index('documentType', ['documentType'], {})
@Entity('pdf_template', {}, { label: 'PDF Template' })
export class PdfTemplateEntity extends BaseEntity {
	/** See {@link PdfTemplateDocumentType}. */
	@Column('varchar', { name: 'documentType', length: 32 })
	public documentType!: string;

	@Column('varchar', { name: 'name', length: 128 })
	public name!: string;

	/**
	 * The full layout document (regions, widgets, page config) — one JSON
	 * column so a template saves and loads atomically. Shape:
	 * {@link PdfTemplateDefinitionModel} (versioned for forward migration).
	 */
	@Column('json', { name: 'definition' })
	public definition!: PdfTemplateDefinitionModel;

	/**
	 * The tenant-wide fallback for this document type when the document's
	 * company has no assigned template. At most one default per document
	 * type — enforced in the service, not the schema, because the sync
	 * process cannot express partial unique indexes.
	 */
	@Column('tinyint', { name: 'isDefault', width: 1, default: () => "'0'" })
	public isDefault!: boolean;

	/** Inactive templates are kept for editing but never used to render. */
	@Column('tinyint', { name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@OneToMany(
		() => PdfTemplateCompanyEntity,
		(assignment) => assignment.fkPdfTemplate,
	)
	public companyAssignments!: Array<PdfTemplateCompanyEntity>;
}
